import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import * as Http from "../../../http";
import { ProgressBar } from "react-bootstrap";
import * as Business from "../../../utils/business";
import { ErrorCode, URL as ApiURL } from "../../../const";
import { FileExtension } from "../../../const";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import MessageBox from "../../../components/MessageBox";

const chunkSize = 1048576 * 3;

// List of required input files for Liquidity V2
const REQUIRED_FILES = [
  "IN_CARD_LOAN",
  "IN_COLLATERAL",
  "IN_CUSTOMER_LOAN",
  "IN_DEPOSIT_BORROWING",
  "IN_FACILITIES",
  "IN_FORWARD_TRANSACTION",
  "IN_GL",
  "IN_INVESTMENT",
  "IN_MM_BORROWING",
  "IN_MM_LENDING",
  "IN_OPTION_TRANSACTION",
  "IN_OTHER_LR",
  "IN_PAYMENT_SCHEDULE",
  "IN_REPO",
  "IN_SPOT_POSITION",
  "IN_SWAP_TRANSACTION",
  "IN_VALUE_PAPER_ISSUANCE",
];

function convertReportName(reportName) {
  return `LIQUIDITY_V2_${reportName}`;
}

function buildValidatePageUrl(reportDate, reportName) {
  const token = Http.getToken() || "";
  const params = new URLSearchParams({
    apiBase: ApiURL.fileV2,
    token,
    reportDate,
    reportName,
    component: "LIQUIDITY_V2",
  });
  return `/validate-result?${params.toString()}`;
}

class UploadFile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      progressValue: 0,
      errorCode: null,

      fileExtension: FileExtension.XLSX,
      isMultipleFile: true, // Allow multiple CSV files
      fileAccept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv",
      reportName: "",
      reportDate: moment().format("YYYY-MM-DD"),
      noFileVal: "No file chosen...",
      classFileUpload: "file-upload",

      totalFiles: 0,
      listFileNameSelected: [],
      dicFileToBeUpload: {},
      validatePageUrl: null,
      currentStep: "form",
      sseErrors: [],
      ssePercent: 0,
      sseDone: false,
      sseStarted: false,
      uploadErrors: [],
      calcResult: null,
      loadingResult: false,
      isDragging: false,
    };
    this.setFileExtensionSelected = this.setFileExtensionSelected.bind(this);
    this.onChangeFile = this.onChangeFile.bind(this);
    this.onUploadFile = this.onUploadFile.bind(this);
    this.onUploadChunkFile = this.onUploadChunkFile.bind(this);
    this.onUploadChunkFileComplete = this.onUploadChunkFileComplete.bind(this);
  }

  _autoNavTimer = null;
  _batchInterval = null;
  _sseSource = null;

  componentWillUnmount() {
    if (this._autoNavTimer) {
      clearTimeout(this._autoNavTimer);
    }
    if (this._batchInterval) {
      clearInterval(this._batchInterval);
    }
    if (this._sseSource) {
      this._sseSource.close();
    }
  }

  setFileExtensionSelected(fileExtension) {
    let fileAccept = "";
    let isMultipleFile = false;

    if (fileExtension === FileExtension.XLSX) {
      fileAccept =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";
      isMultipleFile = false;
    } else if (fileExtension === FileExtension.CSV) {
      fileAccept = ".csv";
      isMultipleFile = true; // Multiple CSV files
    }

    this.setState({
      fileExtension,
      fileAccept,
      isMultipleFile,
      noFileVal: "No file chosen...",
      classFileUpload: "file-upload",
      dicFileToBeUpload: {},
      listFileNameSelected: [],
      totalFiles: 0,
    });
  }

  processFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const dicFileToBeUpload = {};
    const listFileNameSelected = [];
    let totalFiles = 0;
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const totalCount =
        file.size % chunkSize === 0
          ? file.size / chunkSize
          : Math.floor(file.size / chunkSize) + 1;
      const fileID = uuidv4() + "." + file.name.split(".").pop();
      dicFileToBeUpload[file.name] = {
        counter: 1,
        fileName: file.name,
        fileToBeUpload: file,
        beginingOfTheChunk: 0,
        endOfTheChunk: chunkSize,
        fileGuid: fileID,
        fileSize: file.size,
        chunkCount: totalCount > 0 ? totalCount : 1,
        isUploaded: false,
      };
      listFileNameSelected.push(file.name);
      totalFiles++;
    }
    const noFileVal =
      totalFiles > 1 ? `${totalFiles} files chosen` : fileList[0].name;
    this.setState({
      classFileUpload: "file-upload active",
      listFileNameSelected,
      dicFileToBeUpload,
      noFileVal,
      totalFiles,
    });
  };

  onChangeFile = (e) => {
    const files = e.target ? e.target.files : null;
    this.processFiles(files);
  };

  onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
    if (this.state.progressValue > 0) return;
    const files = e.dataTransfer ? e.dataTransfer.files : null;
    this.processFiles(files);
  };

  onUploadFile = async () => {
    const { reportName, reportDate, listFileNameSelected } = this.state;

    if (!reportName || !reportDate) {
      alert("Please fill in Report Name and Report Date");
      return;
    }

    if (listFileNameSelected.length === 0) {
      alert("Please select files to upload");
      return;
    }

    this.setState({ currentStep: "processing", progressValue: 5 });

    const firstFileName = listFileNameSelected[0];
    this.setState({ fileUploading: firstFileName }, () => {
      this.onUploadChunkFile();
    });
  };

  getCurrentFileUploadingInfo = () => {
    const { fileUploading, dicFileToBeUpload } = this.state;
    const fileUploadingInfo = dicFileToBeUpload.hasOwnProperty(fileUploading)
      ? dicFileToBeUpload[fileUploading]
      : {};
    return fileUploadingInfo;
  };

  updateDicFileToBeUpload = (fileName, data, cb) => {
    const dicFileToBeUpload = this.state.dicFileToBeUpload;
    if (dicFileToBeUpload.hasOwnProperty(fileName)) {
      dicFileToBeUpload[fileName] = { ...dicFileToBeUpload[fileName], ...data };
    }
    this.setState({ dicFileToBeUpload }, () => {
      cb && cb();
    });
  };

  onUploadChunkFile = () => {
    try {
      let fileUploadingInfo = this.getCurrentFileUploadingInfo();
      const fileUploading = this.state.fileUploading;
      const currentCounter = fileUploadingInfo.counter;
      fileUploadingInfo["counter"] = currentCounter + 1;

      this.updateDicFileToBeUpload(
        fileUploading,
        fileUploadingInfo,
        async () => {
          fileUploadingInfo = this.getCurrentFileUploadingInfo();
          const {
            fileGuid,
            chunkCount,
            fileToBeUpload,
            beginingOfTheChunk,
            endOfTheChunk,
          } = fileUploadingInfo;

          if (currentCounter <= chunkCount) {
            const chunk = fileToBeUpload.slice(
              beginingOfTheChunk,
              endOfTheChunk,
            );

            Http.uploadChuckFile(chunk, {
              id: currentCounter,
              fileName: fileGuid,
            }).then(async (response) => {
              const isSuccess = response ? response.Success : false;
              if (isSuccess) {
                fileUploadingInfo["beginingOfTheChunk"] = endOfTheChunk;
                fileUploadingInfo["endOfTheChunk"] = endOfTheChunk + chunkSize;
                this.updateDicFileToBeUpload(
                  fileUploading,
                  fileUploadingInfo,
                  async () => {
                    fileUploadingInfo = this.getCurrentFileUploadingInfo();
                    if (currentCounter == chunkCount) {
                      fileUploadingInfo["isUploaded"] = true;
                      this.updateDicFileToBeUpload(
                        fileUploading,
                        fileUploadingInfo,
                        async () => {
                          let isAllFilesUploaded = true;
                          for (let fileName in this.state.dicFileToBeUpload) {
                            if (
                              this.state.dicFileToBeUpload[fileName][
                                "isUploaded"
                              ] === false
                            ) {
                              isAllFilesUploaded = false;
                              break;
                            }
                          }
                          await this.onUploadChunkFileComplete(
                            isAllFilesUploaded,
                          );
                        },
                      );
                    } else {
                      var percentage =
                        (this.state.progressValue /
                          Object.keys(this.state.dicFileToBeUpload).length +
                          (currentCounter / chunkCount) * 100) /
                        Object.keys(this.state.dicFileToBeUpload).length;
                      if (percentage > 99) percentage = 100;
                      this.setState({ progressValue: percentage }, () => {
                        this.onUploadChunkFile();
                      });
                    }
                  },
                );
              } else {
                this.setState({
                  errorCode: ErrorCode.UnknowError,
                  uploadErrors: [{ message: "Upload failed" }],
                });
              }
            });
          }
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  onUploadChunkFileComplete = async (isAllFilesUploaded) => {
    const { reportName, reportDate, fileExtension } = this.state;
    const fileUploadingInfo = this.getCurrentFileUploadingInfo();
    const { fileToBeUpload, fileGuid } = fileUploadingInfo;

    try {
      const response = await Http.uploadChuckFileComplete(
        fileGuid,
        {
          originFileName: fileToBeUpload ? fileToBeUpload.name : fileGuid,
          fileGuid: fileGuid,
          reportName: convertReportName(reportName),
          reportDate: reportDate,
          isAllFilesUploaded,
          fileExtension,
          isUploadOnly: "false",
          isUploadAndMerge: "false",
        },
        Business.COMPONENT.LIQUIDITY_V2,
      );

      if (response && response.ErrorCode === ErrorCode.PendingUpload) {
        // More files to upload - find current file's index then upload the next one
        const listFileNameSelected = this.state.listFileNameSelected;
        const uploadedIndex = listFileNameSelected.indexOf(
          this.state.fileUploading,
        );

        if (
          uploadedIndex >= 0 &&
          uploadedIndex < listFileNameSelected.length - 1
        ) {
          const nextFileName = listFileNameSelected[uploadedIndex + 1];
          this.setState({ fileUploading: nextFileName }, () => {
            this.onUploadChunkFile();
          });
        }
      } else {
        // All files uploaded, start SSE validation
        this.setState({ progressValue: 100 }, () => {
          this.startSseValidation();
        });
      }
    } catch (error) {
      this.setState({
        errorCode: ErrorCode.UnknowError,
        uploadErrors: [{ message: error.message }],
      });
    }
  };

  startSseValidation = () => {
    const { reportDate, reportName } = this.state;
    if (!reportDate || !reportName) return;
    if (this._sseSource) {
      this._sseSource.close();
      this._sseSource = null;
    }
    this.setState({
      sseErrors: [],
      ssePercent: 0,
      sseDone: false,
      sseStarted: false,
    });
    const token = Http.getToken() || "";
    const url = `${ApiURL.fileV2}/validate-data-stream?reportDate=${encodeURIComponent(reportDate)}&reportName=${encodeURIComponent(convertReportName(reportName))}&component=LIQUIDITY_V2&jwt=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    this._sseSource = es;
    es.addEventListener("start", () => {
      this.setState({ sseStarted: true });
    });
    es.addEventListener("progress", (e) => {
      this.setState({ ssePercent: JSON.parse(e.data).percent || 0 });
    });
    es.addEventListener("file", (e) => {
      const d = JSON.parse(e.data);
      if (d.errorCount > 0)
        this.setState((p) => ({ sseErrors: [...p.sseErrors, d] }));
    });
    es.addEventListener("done", async () => {
      this.setState({ sseDone: true, ssePercent: 100, loadingResult: true });
      es.close();
      this._sseSource = null;
      // Fetch calculation result
      try {
        const { reportDate, reportName } = this.state;
        const res = await Http.getLiquidityV2Report(
          reportDate,
          convertReportName(reportName),
        );
        const row =
          res && res.Success && res.Data && res.Data[0] ? res.Data[0] : null;
        this.setState({ calcResult: row, loadingResult: false });
      } catch (e) {
        this.setState({ loadingResult: false });
      }
    });
    es.onerror = () => {
      this.setState({ sseDone: true });
      es.close();
      this._sseSource = null;
    };
  };

  render() {
    const {
      fileExtension,
      fileAccept,
      isMultipleFile,
      reportName,
      reportDate,
      noFileVal,
      classFileUpload,
      currentStep,
      progressValue,
      ssePercent,
      sseDone,
      sseErrors,
      sseStarted,
      uploadErrors,
      calcResult,
      loadingResult,
    } = this.state;

    if (currentStep === "processing") {
      const formatMoney = (v) =>
        v == null ? "-" : new Intl.NumberFormat("vi-VN").format(Math.round(v));
      const StatusBadge = ({ status }) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: 4,
            fontWeight: 700,
            fontSize: 12,
            background: status === "PASS" ? "#d4edda" : "#f8d7da",
            color: status === "PASS" ? "#155724" : "#721c24",
          }}
        >
          {status}
        </span>
      );
      const RatioTable = ({
        title,
        rows,
        ratioLabel,
        ratio,
        minimum,
        status,
      }) => (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              marginBottom: 8,
              padding: "6px 12px",
              background: "#FFF9C4",
              borderLeft: "4px solid #FFE600",
              borderRadius: "0 4px 4px 0",
            }}
          >
            {title}
          </div>
          <table
            className="table table-bordered table-condensed"
            style={{ fontSize: 13, marginBottom: 0 }}
          >
            <tbody>
              {rows.map(([label, value, highlight]) => (
                <tr
                  key={label}
                  style={highlight ? { background: "#fffbe6" } : {}}
                >
                  <td
                    style={{ fontWeight: highlight ? 700 : 400, width: "60%" }}
                  >
                    {label}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: highlight ? 700 : 400,
                    }}
                  >
                    {value}
                  </td>
                </tr>
              ))}
              <tr style={{ background: "#f5f5f5" }}>
                <td style={{ fontWeight: 700 }}>{ratioLabel}</td>
                <td
                  style={{ textAlign: "right", fontWeight: 700, fontSize: 15 }}
                >
                  {ratio}
                  <span style={{ fontSize: 11, marginLeft: 6, color: "#888" }}>
                    / tối thiểu {minimum}%
                  </span>
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 700 }}>Trạng thái</td>
                <td style={{ textAlign: "right" }}>
                  <StatusBadge status={status} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );

      const lcr = calcResult ? calcResult.lcr_data || {} : null;
      const nsfr = calcResult ? calcResult.nsfr_data || {} : null;
      const lcrRatio =
        lcr && lcr.lcr_ratio != null ? `${lcr.lcr_ratio.toFixed(2)}%` : "-";
      const nsfrRatio =
        nsfr && nsfr.nsfr_ratio != null
          ? `${nsfr.nsfr_ratio.toFixed(2)}%`
          : "-";

      return (
        <div className="card">
          <div className="header">
            <h4 className="title">
              <button
                type="button"
                className="btn btn-default btn-xs"
                style={{ marginRight: 12 }}
                onClick={() => {
                  if (this._sseSource) {
                    this._sseSource.close();
                    this._sseSource = null;
                  }
                  this.setState({ currentStep: "form" });
                }}
              >
                ← Quay lại
              </button>
              Kết quả kiểm tra dữ liệu
            </h4>
          </div>
          <div className="content">
            {progressValue > 0 && progressValue < 100 && (
              <div style={{ marginBottom: 16 }}>
                <ProgressBar
                  striped
                  animated
                  now={progressValue}
                  label={`Đang upload ${progressValue.toFixed(1)}%`}
                />
              </div>
            )}
            {progressValue === 100 && sseStarted && !sseDone && (
              <div style={{ marginBottom: 16 }}>
                <ProgressBar
                  striped
                  animated
                  now={ssePercent || 5}
                  label={`Đang kiểm tra dữ liệu ${(ssePercent || 0).toFixed(1)}%`}
                />
              </div>
            )}
            {progressValue === 100 && !sseStarted && !sseDone && (
              <div style={{ marginBottom: 16 }}>
                <ProgressBar striped animated now={100} label="Đang xử lý..." />
              </div>
            )}
            {(sseErrors.length > 0 || uploadErrors.length > 0) && (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 16,
                  padding: "10px 14px",
                  background: "#fff3f3",
                  border: "1px solid #f5c6cb",
                  borderRadius: 4,
                }}
              >
                <p style={{ color: "red", fontWeight: 600, marginBottom: 4 }}>
                  ✗ Phát hiện lỗi dữ liệu (
                  {sseErrors.reduce((s, f) => s + f.errorCount, 0)} lỗi)
                </p>
                {sseErrors.slice(0, 5).map((f) => (
                  <p
                    key={f.sheetName}
                    style={{ fontSize: 12, marginBottom: 2, color: "#721c24" }}
                  >
                    • {f.sheetName}: {f.errorCount} lỗi
                  </p>
                ))}
              </div>
            )}
            {sseDone && sseErrors.length === 0 && uploadErrors.length === 0 && (
              <p style={{ color: "green", fontWeight: 600, marginBottom: 12 }}>
                ✓ Không phát hiện lỗi dữ liệu
              </p>
            )}
            {sseDone && loadingResult && (
              <p style={{ color: "#888", fontStyle: "italic" }}>
                Đang tải kết quả tính toán...
              </p>
            )}
            {sseDone && calcResult && (
              <div>
                {lcr && (
                  <RatioTable
                    title="LIQ1 – LCR (Liquidity Coverage Ratio)"
                    rows={[
                      [
                        "HQLA (High Quality Liquid Assets)",
                        formatMoney(lcr.total_hqla),
                        true,
                      ],
                      [
                        "Tổng dòng tiền ra 30 ngày",
                        formatMoney(lcr.total_outflow),
                      ],
                      [
                        "Tổng dòng tiền vào 30 ngày",
                        formatMoney(lcr.total_inflow),
                      ],
                      ["Dòng tiền ra ròng", formatMoney(lcr.net_cash_outflow)],
                    ]}
                    ratioLabel="Tỷ lệ LCR"
                    ratio={lcrRatio}
                    minimum={lcr.minimum_required || 100}
                    status={lcr.status || "-"}
                  />
                )}
                {nsfr && (
                  <RatioTable
                    title="LIQ2 – NSFR (Net Stable Funding Ratio)"
                    rows={[
                      [
                        "Nguồn vốn ổn định sẵn có (ASF)",
                        formatMoney(nsfr.total_asf),
                        true,
                      ],
                      [
                        "Nhu cầu vốn ổn định (RSF)",
                        formatMoney(nsfr.total_rsf),
                      ],
                    ]}
                    ratioLabel="Tỷ lệ NSFR"
                    ratio={nsfrRatio}
                    minimum={nsfr.minimum_required || 100}
                    status={nsfr.status || "-"}
                  />
                )}
                <div style={{ marginTop: 20, textAlign: "center" }}>
                  <button
                    onClick={() =>
                      this.props.history.push("/liquidity-v2/reports")
                    }
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#FFE600",
                      borderColor: "#7F7E82",
                      borderRadius: "5px",
                      fontWeight: 600,
                      color: "#000",
                      padding: "8px 24px",
                    }}
                  >
                    Xem chi tiết báo cáo →
                  </button>
                </div>
              </div>
            )}
            {sseDone && !calcResult && !loadingResult && (
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <p style={{ color: "#888" }}>Không có kết quả tính toán</p>
                <button
                  onClick={() =>
                    this.props.history.push("/liquidity-v2/reports")
                  }
                  className="btn btn-default"
                  style={{ marginTop: 8 }}
                >
                  Xem danh sách báo cáo
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    const { listFileNameSelected } = this.state;
    const checkedSet = new Set(
      listFileNameSelected.map((f) => f.replace(/\.[^.]+$/, "").toUpperCase()),
    );
    const checkedCount = REQUIRED_FILES.filter((r) => checkedSet.has(r)).length;
    const allChecked = checkedCount === REQUIRED_FILES.length;

    return (
      <div className="card upload-form-animated">
        <div className="header">
          <h4 className="title">Upload file</h4>
          <button
            type="button"
            className="btn btn-default btn-sm"
            style={{
              marginLeft: 12,
              borderRadius: 20,
              fontSize: 12,
              padding: "4px 14px",
              border: "1px solid #7F7E82",
            }}
            onClick={() => Http.downloadSampleFile("LCR.zip", "SAMPLE")}
          >
            <i className="pe-7s-download" style={{ marginRight: 4 }} />
            Download Template
          </button>
        </div>
        <div style={{ height: 10 }} />
        <div className="content" style={{ paddingBottom: 0 }}>
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            {/* ── LEFT: Upload Form ── */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <form>
                <div className="form-group">
                  <label style={{ fontSize: 13, marginBottom: 4 }}>
                    File format
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {Object.keys(FileExtension).map((ext, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => this.setFileExtensionSelected(ext)}
                        style={{
                          padding: "5px 16px",
                          borderRadius: 20,
                          border:
                            fileExtension === ext
                              ? "2px solid #1a1a3e"
                              : "1px solid #ccc",
                          background:
                            fileExtension === ext ? "#1a1a3e" : "#fff",
                          color: fileExtension === ext ? "#fff" : "#333",
                          fontWeight: fileExtension === ext ? 700 : 400,
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        .{ext.toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 13, marginBottom: 4 }}>
                    Data file
                    <span style={{ color: "#C00F0C", marginLeft: 4 }}>*</span>
                  </label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (progressValue === 0)
                        this.setState({ isDragging: true });
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (progressValue === 0)
                        this.setState({ isDragging: true });
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.setState({ isDragging: false });
                    }}
                    onDrop={this.onDrop}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: this.state.isDragging
                        ? "2px dashed #FFE600"
                        : "1px dashed #bbb",
                      borderRadius: 8,
                      padding: "10px 14px",
                      background: this.state.isDragging ? "#fffef0" : "#fafafa",
                      gap: 10,
                      cursor: progressValue > 0 ? "not-allowed" : "pointer",
                      transition: "border 0.15s, background 0.15s",
                    }}
                    onClick={() => {
                      if (progressValue === 0)
                        document.getElementById("chooseFile").click();
                    }}
                  >
                    <i
                      className="pe-7s-upload"
                      style={{
                        fontSize: 22,
                        color: this.state.isDragging ? "#FFE600" : "#1a1a3e",
                        transition: "color 0.15s",
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color:
                            noFileVal === "No file chosen..."
                              ? "#aaa"
                              : "#1E1E1E",
                          fontWeight:
                            noFileVal === "No file chosen..." ? 400 : 600,
                        }}
                      >
                        {this.state.isDragging
                          ? "Thả file vào đây..."
                          : noFileVal}
                      </div>
                      <div
                        style={{ fontSize: 11, color: "#888", marginTop: 2 }}
                      >
                        {fileExtension === FileExtension.CSV
                          ? "Kéo thả hoặc click để chọn nhiều file CSV (Ctrl+A để chọn tất cả)"
                          : "Kéo thả hoặc click để chọn file"}
                      </div>
                    </div>
                    <label
                      htmlFor="chooseFile"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        margin: 0,
                        padding: "6px 14px",
                        borderRadius: 6,
                        background: progressValue > 0 ? "#eee" : "#FFE600",
                        color: progressValue > 0 ? "#aaa" : "#1a1a3e",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: progressValue > 0 ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Chọn file
                    </label>
                    <input
                      type="file"
                      multiple={isMultipleFile}
                      name="chooseFile"
                      id="chooseFile"
                      disabled={progressValue > 0}
                      accept={fileAccept}
                      onChange={this.onChangeFile}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 16 }}>
                  <div className="form-group" style={{ flex: 2 }}>
                    <label style={{ fontSize: 13, marginBottom: 4 }}>
                      Report Name
                      <span style={{ color: "#C00F0C", marginLeft: 4 }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="reportname"
                      value={reportName}
                      onChange={(event) => {
                        this.setState({ reportName: event.target.value });
                      }}
                      style={{
                        border: "1px solid #D9D9D9",
                        borderRadius: 8,
                        height: 40,
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label style={{ fontSize: 13, marginBottom: 4 }}>
                      Report Date
                      <span style={{ color: "#C00F0C", marginLeft: 4 }}>*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="reportDate"
                      min="2010-01-01"
                      max={moment(new Date(), "YYYY-MM-DD").format(
                        "YYYY-MM-DD",
                      )}
                      value={reportDate}
                      onChange={(event) => {
                        this.setState({ reportDate: event.target.value });
                      }}
                      style={{
                        border: "1px solid #D9D9D9",
                        borderRadius: 8,
                        height: 40,
                      }}
                    />
                  </div>
                </div>
              </form>

              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  disabled={progressValue > 0}
                  className="btn btn-primary upload-btn-modern"
                  style={{
                    backgroundColor: "#FFE600",
                    borderColor: "#7F7E82",
                    borderRadius: 8,
                    fontWeight: 700,
                    color: "#1a1a3e",
                    padding: "8px 28px",
                    fontSize: 14,
                  }}
                  onClick={this.onUploadFile}
                >
                  <i
                    className="pe-7s-upload"
                    style={{ marginRight: 6, fontWeight: 700 }}
                  />
                  Upload
                </button>
              </div>
            </div>

            {/* ── RIGHT: Required Files Checklist ── */}
            <div
              style={{
                width: 270,
                flexShrink: 0,
                border: "1px solid #e8e8e8",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              {/* header */}
              <div
                style={{
                  background: "#1a1a3e",
                  padding: "10px 14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                  Required Files
                </span>
                <span
                  style={{
                    background: "#FFE600",
                    color: "#1a1a3e",
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {checkedCount}/{REQUIRED_FILES.length}
                </span>
              </div>

              {/* progress bar */}
              <div style={{ height: 3, background: "#eee" }}>
                <div
                  style={{
                    height: 3,
                    width: `${(checkedCount / REQUIRED_FILES.length) * 100}%`,
                    background: "#FFE600",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>

              {/* file list */}
              <div style={{ maxHeight: 380, overflowY: "auto" }}>
                {REQUIRED_FILES.map((reqFile, idx) => {
                  const checked = checkedSet.has(reqFile);
                  return (
                    <div
                      key={reqFile}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 14px",
                        borderBottom: "1px solid #f2f2f2",
                        borderLeft: checked
                          ? "3px solid #FFE600"
                          : "3px solid transparent",
                        background: checked ? "#fffef5" : "#fff",
                        transition: "all 0.15s",
                      }}
                    >
                      {/* Icon only — no box */}
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {checked ? (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M3 9.5L7.2 14L15 5"
                              stroke="#FFE600"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 8 8"
                            fill="none"
                          >
                            <circle
                              cx="4"
                              cy="4"
                              r="3"
                              stroke="#d0d0d0"
                              strokeWidth="1.5"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          color: checked ? "#1a1a3e" : "#999",
                          fontWeight: checked ? 700 : 400,
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {reqFile}.csv
                      </span>
                      {checked && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          style={{ flexShrink: 0 }}
                        >
                          <circle cx="6" cy="6" r="5" fill="#FFE600" />
                          <path
                            d="M3.5 6L5.2 8L8.5 4"
                            stroke="#1a1a3e"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  padding: "8px 14px",
                  background: allChecked ? "#fffef5" : "#fafafa",
                  borderTop: "1px solid #eee",
                  borderLeft: allChecked
                    ? "3px solid #FFE600"
                    : "3px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    color: allChecked ? "#1a1a3e" : "#aaa",
                    margin: 0,
                    fontWeight: allChecked ? 700 : 400,
                  }}
                >
                  {allChecked
                    ? "Đã đủ file. Nhấn Upload để tiếp tục."
                    : `Còn thiếu ${REQUIRED_FILES.length - checkedCount} file.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(UploadFile);



// WEBPACK FOOTER //
// src/pages/LiquidityV2/UploadFile/index.js